import { formatKvittering, formatKvitteringToValidIds } from './utils'
import { IKvitteringActions, SAVING } from './types'
import { fetchKvitteringById$ } from './helper'
import { ofType, combineEpics, StateObservable, Epic } from 'redux-observable'
import { AnyAction } from 'redux'
import {
  withLatestFrom,
  mergeMap,
  map,
  catchError,
  filter,
  debounce,
} from 'rxjs/operators'
import { of, Observable, concat, merge, EMPTY, interval } from 'rxjs'
import { kvitteringActions, kvitteringActionTypes } from './actions'
import { IStoreState } from '../../reducers/types'
import { ICommonApi } from '../../api'
import { dangerToast } from '../../common/toast'
import { createQueryStringFromObject } from '../../common/createQueryStringFromObject'
import { isUuid } from '../../common/string-utils'
import { navigate } from '../../common/navigation'
import { ENDPOINT } from '../../constants'

export const ROOT_URL_TILSYNKVITTERING = '/api/tilsynskvittering-api/v1'
export const ROOT_URL_VIRKSOMHET = '/api/virksomhet-api'
export const ROOT_URL_TILSYNSOBJEKT = '/api/tilsynsobjekt-api'

export const fetchKvitteringByIdEpic =
  (commonApi: ICommonApi): Epic<IKvitteringActions, AnyAction, IStoreState> =>
  (action$: Observable<AnyAction>, state$: StateObservable<IStoreState>) =>
    action$.pipe(
      ofType(kvitteringActionTypes.FETCH_KVITTERING_BY_ID),
      withLatestFrom(state$),
      filter(() => window.navigator.onLine),
      mergeMap(([{ id }, state]) => {
        return of(state).pipe(
          fetchKvitteringById$(commonApi, state)(id),
          mergeMap((kvittering: any) =>
            concat(
              of(kvitteringActions.setCurrentKvittering(kvittering)),
              of(kvitteringActions.fetchKvitteringByIdSuccess(kvittering))
            )
          ),
          catchError((err) => {
            return of(kvitteringActions.fetchKvitteringByIdFailed(err.message))
          })
        )
      })
    )

export const fetchSammenstillingEpic =
  (commonApi: ICommonApi) =>
  (action$: Observable<AnyAction>, state$: StateObservable<IStoreState>) =>
    action$.pipe(
      ofType(kvitteringActionTypes.FETCH_SAMMENSTILLING),
      withLatestFrom(state$),
      filter(() => window.navigator.onLine),
      debounce(([, { kvittering }]) =>
        kvittering.selectedTilsynsobjekt.status === SAVING ||
        kvittering.selectedKvittering.status === SAVING
          ? interval(2000)
          : interval(0)
      ),
      mergeMap(([, state]) => {
        const kvitteringId = state.kvittering.selectedKvittering.data.id
        const url = `${ENDPOINT.TILSYNSKVITTERING}/tilsynskvitteringer/${kvitteringId}/sammenstilling`
        return of(state).pipe(
          commonApi.get(url, state),
          map((resp: any) => resp || {}),
          map((kvittering: any) =>
            kvitteringActions.fetchSammenstillingSuccess(
              formatKvittering(kvittering)
            )
          ),
          catchError((err) =>
            of(kvitteringActions.fetchSammenstillingFailed(err.message))
          )
        )
      })
    )

export const updateCurrentKvitteringEpic =
  (commonApi: ICommonApi) =>
  (action$: Observable<AnyAction>, state$: StateObservable<IStoreState>) =>
    action$.pipe(
      ofType(kvitteringActionTypes.UPDATE_CURRENT_KVITTERING),
      withLatestFrom(state$),
      filter(() => window.navigator.onLine),
      filter(
        ([, { kvittering }]) =>
          kvittering.selectedKvittering.data?._links?.self?.href
      ),
      debounce(([{ delay }]) => interval(delay)),
      mergeMap(([{ payload }, state]) => {
        const { kvittering } = state
        const URL = `${ENDPOINT.TILSYNSKVITTERING}/tilsynskvitteringer/${kvittering.selectedKvittering.data.id}`
        const getPayload = (payload) => {
          if (isUuid(payload?.bilderGjennomgaattBegrunnelse)) {
            return {
              bilderGjennomgaattBegrunnelse: null,
            }
          }
          if (isUuid(payload?.registrertInnholdGjennomgaattBegrunnelse)) {
            return {
              registrertInnholdGjennomgaattBegrunnelse: null,
            }
          }
          return payload
        }
        return of(state).pipe(
          commonApi.put(URL, state, {
            ...formatKvitteringToValidIds(kvittering.selectedKvittering.data),
            ...getPayload(payload),
          }),
          mergeMap(() => {
            const shouldGetLatestKvitteringData = payload?.status === 'ARKIVERT'
            const id = kvittering.selectedKvittering.data.id!
            return concat(
              shouldGetLatestKvitteringData
                ? of(kvitteringActions.fetchKvitteringById(id))
                : EMPTY,
              of(kvitteringActions.updateCurrentKvitteringSuccess())
            )
          }),
          catchError((err) => {
            return concat(
              of(kvitteringActions.updateCurrentKvitteringFailed(err.message)),
              of(dangerToast())
            )
          })
        )
      })
    )

export const fetchKvitteringFromIdAndUpdateCurrentKvitteringEpic =
  (commonApi: ICommonApi) =>
  (action$: Observable<AnyAction>, state$: StateObservable<IStoreState>) =>
    action$.pipe(
      ofType(kvitteringActionTypes.SET_CURRENT_KVITTERING_FROM_ID),
      withLatestFrom(state$),
      filter(() => window.navigator.onLine),
      mergeMap(([{ tilsynskvitteringId, expand }, state]) => {
        const URL = `${ENDPOINT.TILSYNSKVITTERING}/tilsynskvitteringer/${tilsynskvitteringId}`
        const URLwithQueries = URL.concat(
          createQueryStringFromObject('?')({ expand })
        )
        return of(state).pipe(
          commonApi.get(URLwithQueries, state),
          map((resp) => kvitteringActions.setCurrentKvittering(resp)),
          catchError((err) => {
            if (err.status === 404) {
              navigate('/')
              return concat(
                of(
                  kvitteringActions.updateCurrentKvitteringFailed(err.message)
                ),
                of(dangerToast('Kvitteringen finnes ikke'))
              )
            } else {
              return concat(
                of(
                  kvitteringActions.updateCurrentKvitteringFailed(err.message)
                ),
                of(dangerToast())
              )
            }
          })
        )
      })
    )

export const fetchTilsynstyperEpic =
  (commonApi: ICommonApi) =>
  (action$: Observable<AnyAction>, state$: StateObservable<IStoreState>) =>
    action$.pipe(
      ofType(kvitteringActionTypes.FETCH_TILSYNSTYPER),
      withLatestFrom(state$),
      mergeMap(([, state]) => {
        return of(state).pipe(
          commonApi.get(
            `${ENDPOINT.TILSYNSKVITTERING}/tilsynskvitteringer/tilsynstyper`,
            state
          ),
          map((resp: any) =>
            resp._embedded ? resp._embedded.tilsynstypeList : []
          ),
          mergeMap((tilsynstyper) => {
            return merge(
              of(kvitteringActions.fetchTilsynstyperSuccess(tilsynstyper))
            )
          }),
          catchError((err) =>
            of(kvitteringActions.fetchTilsynstyperFailed(err))
          )
        )
      })
    )

export const fetchKvitteringerFraKollegaEpic =
  (commonApi: ICommonApi) =>
  (action$: Observable<AnyAction>, state$: StateObservable<IStoreState>) =>
    action$.pipe(
      ofType(kvitteringActionTypes.FETCH_KVITTERINGER_FRA_KOLLEGA),
      withLatestFrom(state$),
      filter(() => window.navigator.onLine),
      mergeMap(([{ userId }, state]) => {
        const pageSize = 100
        const queries = createQueryStringFromObject('?')({
          'page.size': pageSize,
          'page.number': 0,
        })

        return of(state).pipe(
          commonApi.get(
            `${ENDPOINT.TILSYNSKVITTERING}/tilsynskvitteringer/overtagelse/${userId}${queries}`,
            state
          ),
          map(
            (resp: any) => resp._embedded?.overtagbarTilsynskvitteringList ?? []
          ),
          mergeMap((kvitteringer) =>
            of(
              kvitteringActions.fetchKvitteringerFraKollegaSuccess(kvitteringer)
            )
          ),
          catchError((error) =>
            of(kvitteringActions.fetchKvitteringerFraKollegaFail(error.message))
          )
        )
      })
    )

export default (commonApi: ICommonApi) =>
  combineEpics(
    fetchKvitteringByIdEpic(commonApi),
    fetchSammenstillingEpic(commonApi),
    updateCurrentKvitteringEpic(commonApi),
    fetchKvitteringFromIdAndUpdateCurrentKvitteringEpic(commonApi),
    fetchTilsynstyperEpic(commonApi),
    fetchKvitteringerFraKollegaEpic(commonApi)
  )
